import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAccountAccessToken } from '../lib/accessToken'
import { fetchMgmtProfile, patchUserMetadata } from '../lib/accountApi'
import { formatBrPhone, formatCpf } from '../lib/format'
import { copy } from '../lib/i18n'
import {
  buildProfileFields,
  initials,
  socialIdentities,
  type MgmtUserProfile,
  type ProfileField,
} from '../lib/profile'
import { Button } from '../ui/Button'
import { Field, SelectInput, TextInput, fieldStyles } from '../ui/Field'
import { RangoAlert, RangoFlag } from '../ui/RangoFeedback'
import { RangoLink } from '../ui/RangoLink'
import { RangoModal } from '../ui/RangoModal'
import './account.css'

const EDITABLE_META_KEYS: Record<string, string> = {
  mother_name: 'motherName',
  last_email_update: 'last_email_update',
  anti_fraud_code: 'anti_fraud_code',
  last_phone_update: 'last_phone_update',
  document_number: 'documentNumber',
  prova_vida: 'prova_vida',
  gender: 'gender',
  phone: 'phoneNumber',
  migrar: 'migrar',
  force_mfa: 'force_mfa',
  name: 'fullName',
}

const MAX_LENGTH = 120

/** Campos que nunca aparecem como linha editável (metadados operacionais). */
const HIDDEN_KEYS = new Set([
  'last_email_update',
  'last_phone_update',
  'force_mfa',
  'migrar',
  'external_user_id',
  'user_id',
])

function displayNameFrom(
  mgmt: MgmtUserProfile | null,
  user: ReturnType<typeof useAuth0>['user'],
) {
  const um = (mgmt?.user_metadata || {}) as Record<string, unknown>
  const metaName = typeof um.fullName === 'string' ? um.fullName : undefined
  if (metaName) return metaName
  if (mgmt?.name && mgmt.name !== mgmt.email) return mgmt.name
  if (user?.name && user.name !== user.email) return user.name
  return undefined
}

/** Validação do popup de edição — pura e testável. */
export function validateFieldValue(
  field: ProfileField,
  raw: string,
): string | undefined {
  const value = raw.trim()
  if (field.key === 'name' && !value) return copy.personalInfo.required
  if (value.length > MAX_LENGTH) {
    return copy.personalInfo.tooLong.replace('{max}', String(MAX_LENGTH))
  }
  if (field.key === 'document_number' && value) {
    const digits = value.replace(/\D/g, '')
    if (digits.length !== 11) return 'O CPF precisa ter 11 dígitos.'
  }
  if (field.key === 'phone' && value) {
    const digits = value.replace(/\D/g, '')
    if (digits.length < 10 || digits.length > 13) {
      return 'Informe um celular válido com DDD.'
    }
  }
  return undefined
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/

function humanizeValue(value: string): string {
  if (!value) return ''
  if (value === 'true') return copy.common.yes
  if (value === 'false') return copy.common.no
  if (ISO_DATE.test(value)) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) {
      return value.length === 10
        ? date.toLocaleDateString('pt-BR')
        : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    }
  }
  return value
}

/** Valor exibido na linha (select → label da opção; boolean → Sim/Não). */
export function displayFieldValue(field: ProfileField): string {
  if (field.type === 'select') {
    const option = field.options?.find((o) => o.value === field.value)
    return option?.label || humanizeValue(field.value)
  }
  const digits = field.value.replace(/\D/g, '')
  if (field.key === 'document_number' && digits.length === 11) {
    return formatCpf(field.value)
  }
  if (field.key === 'username' && /^\d{11}$/.test(field.value)) {
    return formatCpf(field.value)
  }
  if (field.key === 'phone' && digits.length >= 10) {
    return formatBrPhone(field.value)
  }
  return humanizeValue(field.value)
}

/**
 * Aplica um patch de `user_metadata` no perfil em memória. Os valores
 * booleanos continuam booleanos (a UI compara com `String(...)`).
 */
export function applyMetadataPatch(
  profile: MgmtUserProfile,
  metaKey: string,
  value: unknown,
): MgmtUserProfile {
  const metadata = { ...(profile.user_metadata || {}) }
  if (metaKey === 'fullName') metadata.fullName = value
  else if (metaKey === 'phoneNumber') metadata.phoneNumber = value
  else metadata[metaKey] = value
  return { ...profile, user_metadata: metadata }
}

type PopupState =
  | { kind: 'none' }
  | { kind: 'alert'; title: string; message: string }
  | { kind: 'edit'; field: ProfileField }

export function PersonalInfoPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [mgmt, setMgmtState] = useState<MgmtUserProfile | null>(null)
  // Guarda o último perfil (evita `setState` velho sobrescrevendo o patch).
  const mgmtRef = useRef<MgmtUserProfile | null>(null)
  const setMgmt = useCallback((next: MgmtUserProfile | null | ((current: MgmtUserProfile | null) => MgmtUserProfile | null)) => {
    const value = typeof next === 'function' ? next(mgmtRef.current) : next
    mgmtRef.current = value
    setMgmtState(value)
  }, [])
  const [loadError, setLoadError] = useState<string>()
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [popup, setPopup] = useState<PopupState>({ kind: 'none' })
  const [notice, setNotice] = useState<string>()

  const refresh = useCallback(async () => {
    setLoadError(undefined)
    try {
      const token = await getAccountAccessToken(getAccessTokenSilently)
      const res = await fetchMgmtProfile(token)
      if (res.error) setLoadError(res.error)
      if (res.profile) setMgmt(res.profile)
    } catch {
      setLoadError(copy.personalInfo.loadError)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const fields = useMemo(() => buildProfileFields(user, mgmt), [user, mgmt])

  const display =
    displayNameFrom(mgmt, user) || user?.nickname || user?.email || 'Usuário'
  const socials = socialIdentities(mgmt?.identities)
  const myAccount = import.meta.env.VITE_AUTH0_MY_ACCOUNT_URL

  const profileRows = fields.filter(
    (f) => f.key !== 'mother_name' && !HIDDEN_KEYS.has(f.key),
  )
  const mother = fields.find((f) => f.key === 'mother_name')

  function openEdit(field: ProfileField) {
    const metaKey = EDITABLE_META_KEYS[field.key]
    if (!metaKey) {
      if (myAccount) window.open(myAccount, '_blank', 'noopener')
      return
    }
    setNotice(undefined)
    setPopup({ kind: 'edit', field })
  }

  async function saveEdit(field: ProfileField, next: string) {
    const metaKey = EDITABLE_META_KEYS[field.key]
    if (!metaKey) return
    setBusyKey(field.key)
    try {
      const token = await getAccountAccessToken(getAccessTokenSilently)
      const payloadValue: unknown =
        field.type === 'boolean' ? next === 'true' : next.trim()
      const res = await patchUserMetadata(token, { [metaKey]: payloadValue })
      if (res.error) {
        setPopup({
          kind: 'alert',
          title: copy.personalInfo.saveError,
          message: copy.personalInfo.saveErrorGeneric,
        })
        return
      }
      if (res.profile?.user_metadata) {
        setMgmt(res.profile)
      } else {
        // Worker respondeu só com ack: mantém o dado patcheado em memória
        // (o próximo GET refresca a verdade do servidor).
        setMgmt((current) =>
          current ? applyMetadataPatch(current, metaKey, payloadValue) : current,
        )
      }
      setPopup({ kind: 'none' })
      setNotice(copy.personalInfo.saveSuccess)
    } finally {
      setBusyKey(null)
    }
  }

  function exportProfile() {
    const payload = mgmt || user
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = copy.personalInfo.exportFile
    anchor.click()
    URL.revokeObjectURL(url)
  }

  function renderRow(field: ProfileField) {
    const value = displayFieldValue(field)
    const metaKey = EDITABLE_META_KEYS[field.key]
    const editable = Boolean(field.editable && metaKey)
    return (
      <div className="acct-row" key={field.key}>
        <div className="acct-row-label">{field.label}</div>
        <div className={value ? 'acct-row-value' : 'acct-row-value empty'}>
          {value || copy.personalInfo.emptyValue}
        </div>
        <div>
          {editable ? (
            <Button
              variant="link"
              className="acct-edit"
              disabled={busyKey === field.key}
              loading={busyKey === field.key}
              onClick={() => openEdit(field)}
              aria-label={copy.personalInfo.edit.replace('{field}', field.label)}
            >
              Editar
            </Button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <>
      <h1 className="acct-title">{copy.personalInfo.title}</h1>
      <p className="acct-sub">{copy.personalInfo.subtitle}</p>

      {notice ? (
        <div className="acct-alert-slot">
          <RangoFlag tone="positive">{notice}</RangoFlag>
        </div>
      ) : null}

      {loadError ? (
        <div className="acct-alert-slot">
          <RangoAlert
            tone="warning"
            title={copy.personalInfo.loadError}
            action={
              <RangoLink onClick={() => void refresh()} className="acct-alert-link">
                {copy.common.retry}
              </RangoLink>
            }
          >
            {copy.personalInfo.loadErrorHint}
          </RangoAlert>
        </div>
      ) : null}

      <section className="acct-card" aria-labelledby="profile-title">
        <div className="acct-card-body">
          <div className="acct-profile-head">
            <div>
              <h2 className="acct-card-title" id="profile-title">
                {copy.personalInfo.profileTitle}
              </h2>
              <p className="acct-card-desc">{copy.personalInfo.profileDesc}</p>
            </div>
            <span className="acct-avatar acct-avatar-lg" aria-hidden="true">
              {initials(display)}
            </span>
          </div>
        </div>
        {profileRows.map(renderRow)}
      </section>

      {mother ? (
        <section className="acct-card acct-card-plain acct-card-spaced">
          <h2 className="acct-card-title acct-card-title-inset">
            {copy.personalInfo.motherTitle}
          </h2>
          {renderRow(mother)}
        </section>
      ) : null}

      <section className="acct-card acct-card-spaced">
        <div className="acct-card-body">
          <h2 className="acct-card-title">{copy.personalInfo.socialTitle}</h2>
          <p className="acct-card-desc">{copy.personalInfo.socialDesc}</p>
          {socials.length > 0 ? (
            <ul className="acct-identity-list">
              {socials.map((id) => (
                <li key={`${id.provider}:${id.user_id}`}>
                  <strong>{id.provider || id.connection}</strong>
                  {id.profileData?.email || id.user_id
                    ? ` — ${id.profileData?.email || id.user_id}`
                    : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="acct-empty">{copy.personalInfo.socialEmpty}</p>
          )}
        </div>
      </section>

      <section className="acct-card acct-card-spaced">
        <div className="acct-card-body">
          <h2 className="acct-card-title">{copy.personalInfo.exportTitle}</h2>
          <p className="acct-card-desc">{copy.personalInfo.exportDesc}</p>
        </div>
        <div className="acct-card-foot">
          <Button variant="link" onClick={exportProfile}>
            {copy.personalInfo.exportAction}
          </Button>
        </div>
      </section>

      <RangoModal
        open={popup.kind === 'alert'}
        title={popup.kind === 'alert' ? popup.title : ''}
        onClose={() => setPopup({ kind: 'none' })}
        footer={
          <Button onClick={() => setPopup({ kind: 'none' })}>
            {copy.common.close}
          </Button>
        }
      >
        {popup.kind === 'alert' ? popup.message : null}
      </RangoModal>

      {popup.kind === 'edit' ? (
        // Remonta o popup a cada abertura e também quando o perfil muda
        // (após salvar): `key` com o valor evita estado stale do formulário.
        <EditFieldModal
          key={popup.field.key}
          field={popup.field}
          saving={busyKey !== null}
          onCancel={() => setPopup({ kind: 'none' })}
          onSubmit={(value) => void saveEdit(popup.field, value)}
        />
      ) : null}
    </>
  )
}

function EditFieldModal({
  field,
  saving,
  onCancel,
  onSubmit,
}: {
  field: ProfileField
  saving: boolean
  onCancel: () => void
  onSubmit: (value: string) => void
}) {
  const initial =
    field.type === 'boolean'
      ? field.value === 'true'
        ? 'true'
        : 'false'
      : field.value || ''
  const [value, setValue] = useState(initial)
  const [error, setError] = useState<string>()

  function submit(event: React.FormEvent) {
    event.preventDefault()
    const invalid = validateFieldValue(field, value)
    if (invalid) {
      setError(invalid)
      return
    }
    setError(undefined)
    onSubmit(value)
  }

  return (
    <RangoModal
      open
      title={copy.personalInfo.editTitle.replace('{field}', field.label)}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            {copy.common.cancel}
          </Button>
          <Button
            type="submit"
            form="acct-edit-form"
            loading={saving}
          >
            {saving ? copy.common.saving : copy.common.save}
          </Button>
        </>
      }
    >
      <form id="acct-edit-form" onSubmit={submit} noValidate>
        <Field
          label={field.label}
          error={error}
          required={field.key === 'name'}
        >
          {({ id, describedBy, required }) =>
            field.type === 'select' ? (
              <SelectInput
                id={id}
                value={value}
                data-autofocus
                aria-describedby={describedBy}
                onChange={(e) => setValue(e.target.value)}
              >
                <option value="">{field.placeholder ?? 'Selecione'}</option>
                {(field.options || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            ) : field.type === 'boolean' ? (
              <SelectInput
                id={id}
                value={value}
                data-autofocus
                aria-describedby={describedBy}
                onChange={(e) => setValue(e.target.value)}
              >
                <option value="true">{copy.common.yes}</option>
                <option value="false">{copy.common.no}</option>
              </SelectInput>
            ) : (
              <TextInput
                id={id}
                required={required}
                value={value}
                autoFocus
                data-autofocus
                placeholder={field.placeholder}
                error={Boolean(error)}
                aria-describedby={describedBy}
                className={fieldStyles.input}
                onChange={(e) => setValue(e.target.value)}
              />
            )
          }
        </Field>
      </form>
    </RangoModal>
  )
}
